import { useFormikContext } from 'formik';
import React, { useEffect, useMemo, useRef } from 'react';
import useTranslate from '../useTranslate';
import {
    OperationalContactDto,
    OperationalContactRoles,
} from '../../store/operationalContactsSlice';

export interface OperationalContactAssignments {
    operationalContactIds: number[];
}

interface Props {
    operationalContacts?: OperationalContactDto[];
    disabled?: boolean;
}

function MansionToOperationalContactAssignmentsForm(props: Readonly<Props>) {
    const strings = useTranslate('MansionToOperationalContactAssign');
    const roleNames = useTranslate('OperationalContactRoleNames');
    const { values, setFieldValue } =
        useFormikContext<OperationalContactAssignments>();

    const allIds = useMemo(
        () =>
            new Set(
                props.operationalContacts?.map(
                    (it) => it.operational_contact_id
                ) ?? []
            ),
        [props.operationalContacts]
    );
    const selectedIds = new Set(
        values.operationalContactIds ?? ([] as number[])
    );

    const allSelected = selectedIds.size === allIds.size;
    const noneSelected = selectedIds.size === 0;
    const partiallySelected = !noneSelected && !allSelected;

    const checkboxAllRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (checkboxAllRef.current) {
            checkboxAllRef.current.indeterminate = partiallySelected;
        }
    }, [partiallySelected]);

    return (
        <table className="data-table" style={{ width: '100%' }}>
            <thead>
                <tr>
                    <th>
                        <div
                            className={
                                'checkbox checkbox-primary checkbox-custom'
                            }
                        >
                            <input
                                ref={checkboxAllRef}
                                type={'checkbox'}
                                className={'styled'}
                                id={'checkbox-all'}
                                checked={allSelected}
                                onChange={toggleAll}
                                disabled={props.disabled}
                            />
                            <label
                                htmlFor={'checkbox-all'}
                                aria-label={strings.checkAll}
                            />
                        </div>
                    </th>
                    <th>{strings.name}</th>
                    <th>{strings.contactInformation}</th>
                    <th>{strings.role}</th>
                </tr>
            </thead>
            <tbody>
                {props.operationalContacts
                    ?.toSorted(
                        (a, b) =>
                            OperationalContactRoles.indexOf(a.role) -
                            OperationalContactRoles.indexOf(b.role)
                    )
                    .map((oc) => {
                        const checked = selectedIds.has(
                            oc.operational_contact_id
                        );
                        const checkboxId = `checkbox-${oc.operational_contact_id}`;
                        return (
                            <tr key={oc.operational_contact_id}>
                                <td>
                                    <div
                                        className={
                                            'checkbox checkbox-primary checkbox-custom'
                                        }
                                    >
                                        <input
                                            type={'checkbox'}
                                            id={checkboxId}
                                            className={'styled'}
                                            checked={checked}
                                            onChange={() =>
                                                toggleOne(
                                                    oc.operational_contact_id
                                                )
                                            }
                                            disabled={props.disabled}
                                        />
                                        <label
                                            htmlFor={checkboxId}
                                            aria-label={strings.checkOneAria}
                                        />
                                    </div>
                                </td>
                                <td>
                                    {oc.first_name && `${oc.first_name} `}
                                    {oc.name}
                                </td>
                                <td style={{ whiteSpace: 'pre-line' }}>
                                    {formatContactInformation(oc)}
                                </td>
                                <td>{roleNames[oc.role]}</td>
                            </tr>
                        );
                    })}
            </tbody>
        </table>
    );

    function toggleOne(id: number) {
        setFieldValue(
            'operationalContactIds',
            toggleId(values.operationalContactIds, id)
        ).then();
    }

    function toggleAll() {
        setFieldValue(
            'operationalContactIds',
            allSelected ? [] : Array.from(allIds)
        ).then();
    }
}

function formatContactInformation(oc: OperationalContactDto) {
    return [oc.email, oc.phone1, oc.phone2]
        .filter((v): v is string => Boolean(v?.trim()))
        .join('\n');
}

function toggleId(ids: number[], id: number) {
    if (!ids) {
        return [id];
    }
    return ids.includes(id) ? ids.filter((it) => it !== id) : [...ids, id];
}

export default MansionToOperationalContactAssignmentsForm;
